// ============================================================
// BLACK OWL - In-app notifications (FB-15 phase 1)
// ============================================================
// Works only while Black Owl is open in a tab: a browser
// notification (if the player opted in and granted permission)
// plus an optional beep, both only when the tab is in the
// background. Real push to a locked phone is phase 2 (PWA + FCM).
// ============================================================

import { useEffect, useState } from "react";
import { useTitleFlash } from "./title-flash";

const PREF_KEY = "blackowl_notify_v1";
const DEFAULT_PREFS = { enabled: false, sound: false };

export const notificationsSupported = () =>
  typeof window !== "undefined" && "Notification" in window;

export function readNotifyPrefs() {
  try {
    return { ...DEFAULT_PREFS, ...JSON.parse(localStorage.getItem(PREF_KEY) || "{}") };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function writeNotifyPrefs(prefs) {
  try {
    localStorage.setItem(PREF_KEY, JSON.stringify(prefs));
  } catch {
    // storage unavailable - preference just won't survive a refresh
  }
}

// Must be called from a click: browsers only show the prompt for a user gesture
export async function requestNotifyPermission() {
  if (!notificationsSupported()) return "unsupported";
  if (Notification.permission !== "default") return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

let audioCtx = null;
function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    audioCtx = audioCtx || new Ctx();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.15, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.3);
    osc.connect(gain).connect(audioCtx.destination);
    osc.start();
    osc.stop(audioCtx.currentTime + 0.3);
  } catch {
    // audio blocked or unavailable - the notification still goes out
  }
}

// Notify about something that happened while the player looked away.
// `tag` collapses repeats of the same kind into one notification.
export function notify(title, body, tag) {
  if (typeof document === "undefined" || !document.hidden) return;
  const prefs = readNotifyPrefs();
  if (prefs.sound) beep();
  if (prefs.enabled && notificationsSupported() && Notification.permission === "granted") {
    try {
      const n = new Notification(title, { body, tag, renotify: true });
      n.onclick = () => { window.focus(); n.close(); };
    } catch {
      // some mobile browsers only allow notifications from a service worker
    }
  }
}

// "(N) Black Owl" title flash for events missed while the tab was hidden.
// Returns `bump` to call once per event; the count clears when the tab is
// visible again.
export function useMissedEventsTitle() {
  const [missed, setMissed] = useState(0);
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) setMissed(0); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);
  useTitleFlash(missed > 0 ? `(${missed}) Black Owl` : "");
  const bump = () => { if (document.hidden) setMissed((n) => n + 1); };
  return bump;
}
